import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';
import ApplicationHeader from 'applications/sports/components/applicationheader/index.js';
import ApplicationFooter from 'applications/common/components/application-footer/index.js';
import AppOverlay from 'applications/sports/components/app-overlay/index.js';
import { usePageReady } from 'applications/sports/app/view/hooks/index.js';

const BettingCenter = MojitoModules.BettingCenter.module;
const betslipSelectionsContext = MojitoModules.Betslip.betslipSelectionsContext;
const { SelectionsContext } = MojitoModules.SelectionButton.Context;
const QuickLinks = MojitoModules.QuickLinks.module;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const { BannerPromotions, PromotionUtils } = MojitoModules.Promotions;
const MatchAccaSlip = MojitoModules.MatchAcca.MatchAccaSlip;
const MatchAccaTypes = MojitoModules.MatchAcca.Types;
const { FlexPane, ImageButton, PostponedRenderer } = MojitoPresentation.Components;
const AccaBar = MojitoModules.AccaBar.module;

export default class AppViewSportsbook extends UIViewImplementation {
    renderPromotions() {
        const { betslipPromotions: promotionsConfig } = this.config;
        if (!PromotionUtils.hasLocationId(promotionsConfig)) {
            return;
        }
        return <BannerPromotions config={promotionsConfig} applicablePage="homepage" />;
    }

    renderSideMenu() {
        if (this.config.hideSideMenu) {
            return null;
        }

        // Postponing the rendering of heavier components to let Promotions create data subscriptions quicker.
        // What happens is that the postponed components will not be part of the initial synchronous rendering task and instead
        // will be rendered as part of a new task, which will execute according to the event loop queue.
        return (
            <FlexPane config={this.config.sideMenuContainer} class="ta-sideMenu">
                <PostponedRenderer priority={1}>
                    <MatchAccaSlip showMode={MatchAccaTypes.SHOW_MODE.AUTO_SHOW_HIDE} />
                    <BettingCenter />
                </PostponedRenderer>
                {this.renderPromotions()}
            </FlexPane>
        );
    }

    renderApplicationHeader() {
        if (this.config.applicationHeader.hide) {
            return null;
        }

        return <ApplicationHeader />;
    }

    renderAccaBar() {
        if (this.config.accaBar.hide) {
            return null;
        }

        return <AccaBar />;
    }

    render() {
        const style = this.style.mainContainer;
        return (
            <SelectionsContext.Provider value={betslipSelectionsContext}>
                <div id="appView" style={style}>
                    {this.renderApplicationHeader()}

                    <main style={this.style.container}>
                        <div style={this.style.content.page}>
                            {this.props.children}
                            <BackToTopButton {...this.props} />
                        </div>
                        {this.renderSideMenu()}
                    </main>
                    <PostponedRenderer priority={2} name={'acca bar'}>
                        {this.renderAccaBar()}
                    </PostponedRenderer>
                    <ApplicationFooterSection {...this.props} />
                </div>
                <PostponedRenderer name={'AppOverlay'} priority={1}>
                    <AppOverlay />
                </PostponedRenderer>
            </SelectionsContext.Provider>
        );
    }
}

AppViewSportsbook.getStyle = function (config, mode, merge) {
    const style = {
        mainContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            minHeight: '100vh',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            ...config.style.mainContainer,
        },
        container: {
            flex: '1 1 auto',
            width: '100%',
            alignSelf: 'center',
            display: 'flex',
            ...config.style.container,
        },
        content: {
            page: {
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
            },
        },
    };

    return merge({}, style);
};

const ApplicationFooterSection = props => {
    const {
        mojitoTools: { config },
    } = props;
    // To prevent content layout shift, we will render footer with delay
    const isPageReady = usePageReady(config.showFooterTimeout ?? 0);

    if (!isPageReady) {
        return null;
    }

    return (
        <PostponedRenderer priority={5}>
            <footer>
                {!config.applicationFooter.hide && <ApplicationFooter />}
                {!config.quickLinks.hide && <QuickLinks />}
            </footer>
        </PostponedRenderer>
    );
};

const BackToTopButton = props => {
    const {
        mojitoTools: { config, stringResolver },
    } = props;

    // To prevent content layout shift, we will render back button with delay
    const isPageReady = usePageReady(config.showBackToTopButtonTimeout ?? 0);

    if (config.backToTopButtonToggle.hide || !isPageReady) {
        return null;
    }

    return (
        <PostponedRenderer priority={6} name={'back to top button'}>
            <ImageButton
                class="ta-backToTopButton"
                config={config.backToTopButtonConfig}
                onClick={() => window.scrollTo(0, 0)}
                label={stringResolver.resolveAndFormatString('$HOME_PAGE.BACK_TO_TOP_BUTTON')}
            />
        </PostponedRenderer>
    );
};
